import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import useCurrentUser from '~/hooks/useCurrentUser';
import useLocalStorage from '~/hooks/useLocalStorage';
import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { promoRoles } from '~/services/PromoService';
import { formatDateWithClock } from '~/services/TimeService';

type RecentVisitType = 'promo' | 'voucher';

export type PromoRecentVisit = { code: string; href: string; visitedAt: string };
export const PROMO_RECENT_VISITS_KEY = 'promo:recent-promo-visits:v1';
export const VOUCHER_RECENT_VISITS_KEY = 'promo:recent-voucher-visits:v1';
const LEGACY_RECENT_VISITS_KEY = 'promo:recent-visits:v1';
/** Same-window localStorage writes do not emit the browser `storage` event. */
const PROMO_RECENT_VISITS_UPDATED_EVENT = 'promo-recent-visits-updated';
const enabledVisitTypes = new Set<RecentVisitType>();

function getStorageKey(type: RecentVisitType): string {
  return type === 'promo' ? PROMO_RECENT_VISITS_KEY : VOUCHER_RECENT_VISITS_KEY;
}

function getLegacyVisits(type: RecentVisitType): Array<PromoRecentVisit> {
  try {
    const legacyVisits = JSON.parse(window.localStorage.getItem(LEGACY_RECENT_VISITS_KEY) || '[]') as Array<
      PromoRecentVisit & { type?: RecentVisitType }
    >;
    return legacyVisits
      .filter((visit) => visit.type === type)
      .map(({ code, href, visitedAt }) => ({ code, href, visitedAt }));
  } catch {
    return [];
  }
}

function recordRecentVisit(
  type: RecentVisitType,
  visit: Omit<PromoRecentVisit, 'visitedAt'>,
): Array<PromoRecentVisit> | undefined {
  if (!enabledVisitTypes.has(type)) {
    return;
  }
  try {
    const storageKey = getStorageKey(type);
    const existing = JSON.parse(window.localStorage.getItem(storageKey) || '[]') as Array<PromoRecentVisit>;
    const next = [
      { ...visit, visitedAt: new Date().toISOString() },
      ...existing.filter((item) => item.code !== visit.code),
    ].slice(0, 50);
    window.localStorage.setItem(storageKey, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(PROMO_RECENT_VISITS_UPDATED_EVENT, { detail: { type, visits: next } }));
    return next;
  } catch {
    // Storage is optional; navigation should never fail because it is unavailable.
  }
}

export function recordPromoVisit(visit: Omit<PromoRecentVisit, 'visitedAt'>) {
  return recordRecentVisit('promo', visit);
}

export function recordVoucherVisit(visit: Omit<PromoRecentVisit, 'visitedAt'>) {
  return recordRecentVisit('voucher', visit);
}

type Props = {
  type?: RecentVisitType;
};

export default function PromoRecentVisits({ type = 'promo' }: Props) {
  const { user } = useCurrentUser();
  const { hasRole } = useUserPromoRoles({ userId: user?.id_member ?? '', enabled: Boolean(user?.id_member) });
  const storageKey = getStorageKey(type);
  const [storedVisits, setVisits] = useLocalStorage(storageKey, []);
  const visits = storedVisits;
  const [showAll, setShowAll] = useState(false);
  const isBetaUser = hasRole(promoRoles.PROMO_BETA_TESTER_ROLE);
  useEffect(() => {
    if (isBetaUser) {
      enabledVisitTypes.add(type);
    }
    return () => {
      enabledVisitTypes.delete(type);
    };
  }, [isBetaUser, type]);
  useEffect(() => {
    if (window.localStorage.getItem(storageKey) === null) {
      setVisits(getLegacyVisits(type));
    }
  }, [setVisits, storageKey, type]);
  useEffect(() => {
    const syncVisits = (event: Event) => {
      const update = (event as CustomEvent<{ type: RecentVisitType; visits: Array<PromoRecentVisit> }>).detail;
      if (update?.type === type && Array.isArray(update.visits)) {
        setVisits(update.visits);
      }
    };

    window.addEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncVisits);
    return () => window.removeEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncVisits);
  }, [setVisits, type]);
  const recent = useMemo(() => visits.slice(0, 10), [visits]);
  if (!isBetaUser || visits.length === 0) {
    return null;
  }
  const remove = (visit: PromoRecentVisit) => setVisits(visits.filter((item) => item !== visit));
  const renderVisit = (visit: PromoRecentVisit) => (
    <Button key={visit.code} variant="text" size="small" href={visit.href}>
      {visit.code}
    </Button>
  );
  const columns: Array<GridColDef<PromoRecentVisit>> = [
    { field: 'code', headerName: 'Code', flex: 1 },
    {
      field: 'visitedAt',
      headerName: 'Visited',
      width: 190,
      valueFormatter: (value) => formatDateWithClock(value),
    },
    {
      field: 'remove',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton aria-label={`Remove ${params.row.code}`} size="small" onClick={() => remove(params.row)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minHeight: 32, overflow: 'hidden' }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Recently visited:
        </Typography>
        {recent.map(renderVisit)}
        {visits.length > 10 && (
          <Button variant="text" size="small" onClick={() => setShowAll(true)}>
            Show all ({visits.length})
          </Button>
        )}
      </Stack>
      <Dialog open={showAll} onClose={() => setShowAll(false)} fullWidth maxWidth="md">
        <DialogTitle>Recently visited {type === 'promo' ? 'promo codes' : 'voucher codes'}</DialogTitle>
        <DialogContent>
          <DataGrid
            autoHeight
            rows={visits}
            columns={columns}
            getRowId={(row) => row.code}
            hideFooter
            pageSizeOptions={[50]}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setShowAll(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
